/**
 * <s-locale-picker> — custom dropdowns (with flags) inside the language/currency modal.
 * Each [data-picker] holds a hidden input, a [data-picker-button] and a [data-picker-list]
 * of [role=option][data-value] buttons. Choosing an option writes the hidden input;
 * the localization form submits both values together. Closing the dialog restores
 * the original selection.
 */
class SLocalePicker extends HTMLElement {
  #ac;

  connectedCallback() {
    this.#ac = new AbortController();
    const { signal } = this.#ac;
    const pickers = [...this.querySelectorAll('[data-picker]')];

    pickers.forEach((picker) => {
      const button = picker.querySelector('[data-picker-button]');
      const list = picker.querySelector('[data-picker-list]');
      const input = picker.querySelector('input[type="hidden"]');
      const label = picker.querySelector('[data-picker-label]');
      if (!button || !list || !input || !label) return;

      picker._initial = { value: input.value, html: label.innerHTML };

      button.addEventListener('click', () => {
        const willOpen = list.hidden;
        pickers.forEach((p) => this.#close(p));
        if (willOpen) this.#open(picker);
      }, { signal });

      list.addEventListener('click', (event) => {
        const option = event.target.closest('[role="option"]');
        if (!option) return;
        this.#select(picker, option);
        this.#close(picker);
        button.focus();
      }, { signal });

      list.addEventListener('keydown', (event) => {
        const options = [...list.querySelectorAll('[role="option"]')];
        const index = options.indexOf(document.activeElement);
        if (event.key === 'ArrowDown') {
          event.preventDefault();
          options[Math.min(index + 1, options.length - 1)]?.focus();
        } else if (event.key === 'ArrowUp') {
          event.preventDefault();
          options[Math.max(index - 1, 0)]?.focus();
        } else if (event.key === 'Escape') {
          event.preventDefault();
          event.stopPropagation();
          this.#close(picker);
          button.focus();
        }
      }, { signal });
    });

    this.addEventListener('click', (event) => {
      pickers.forEach((p) => {
        if (!p.contains(event.target)) this.#close(p);
      });
    }, { signal });

    const dialog = this.closest('dialog');
    dialog?.addEventListener('close', () => {
      pickers.forEach((p) => {
        this.#close(p);
        if (!p._initial) return;
        const input = p.querySelector('input[type="hidden"]');
        input.value = p._initial.value;
        p.querySelector('[data-picker-label]').innerHTML = p._initial.html;
        p.querySelectorAll('[role="option"]').forEach((o) => {
          o.setAttribute('aria-selected', String(o.dataset.value === p._initial.value));
        });
      });
    }, { signal });
  }

  disconnectedCallback() {
    this.#ac?.abort();
  }

  #open(picker) {
    const list = picker.querySelector('[data-picker-list]');
    const button = picker.querySelector('[data-picker-button]');
    list.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    (list.querySelector('[aria-selected="true"]') || list.querySelector('[role="option"]'))?.focus();
  }

  #close(picker) {
    const list = picker.querySelector('[data-picker-list]');
    const button = picker.querySelector('[data-picker-button]');
    if (!list || list.hidden) return;
    list.hidden = true;
    button?.setAttribute('aria-expanded', 'false');
  }

  #select(picker, option) {
    picker.querySelector('input[type="hidden"]').value = option.dataset.value;
    picker.querySelector('[data-picker-label]').innerHTML = option.querySelector('[data-option-label]').innerHTML;
    picker.querySelectorAll('[role="option"]').forEach((o) => o.setAttribute('aria-selected', String(o === option)));
  }
}

if (!customElements.get('s-locale-picker')) customElements.define('s-locale-picker', SLocalePicker);
